/**
 * CSRF cookie reader for the cookie-auth model (Wave 2.4).
 *
 * The gateway issues a JS-readable double-submit CSRF cookie. Its name carries the
 * `__Host-` prefix in production (Secure) and is unprefixed in local dev (plain
 * HTTP) — the prefix depends on the GATEWAY's environment, not the builder's, so we
 * read by base name and prefer the prefixed variant.
 *
 * Two cookies exist:
 *  - `rs_csrf`               — the normal session CSRF token
 *  - `rs_impersonation_csrf` — used only while an admin is impersonating
 *
 * The client selects which to send via the X-CSRF-Token header based on the auth
 * store's impersonation flag (set on impersonate-start, cleared on stop).
 */

/** Read a cookie value by base name, preferring the `__Host-`-prefixed variant. */
export function readCookie(baseName: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const jar = document.cookie.split('; ');
  for (const name of [`__Host-${baseName}`, baseName]) {
    const prefix = `${name}=`;
    const hit = jar.find((c) => c.startsWith(prefix));
    if (hit) return decodeURIComponent(hit.slice(prefix.length));
  }
  return null;
}

/** The CSRF token to send for the current auth mode (session vs impersonation). */
export function getCsrfToken(impersonating: boolean): string | null {
  return readCookie(impersonating ? 'rs_impersonation_csrf' : 'rs_csrf');
}

/** HTTP methods that mutate state and therefore require the CSRF double-submit. */
export function isMutatingMethod(method: string | undefined): boolean {
  if (!method) return false;
  return ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase());
}
